import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Menu, X, Music, LogIn, User as UserIcon } from 'lucide-react';
import { useAuth } from '../app/AuthContext';
import { MusicNavTrigger } from './MusicNavTrigger';

/**
 * Marketing Navbar (Phase 22 IA restructure).
 *
 * Link inventory is now audience-first:
 *   Athletes | Parents | Coaches | Method | Pricing | Journal | About
 *
 * Right-side CTAs:
 *   Quiz (text link) | The Locker Room (pill, kept \u2014 it's a $29.99 product)
 *   | Book a Discovery Call (filled button)
 *
 * Phase 27 \u2014 "Journal" promoted back into the primary nav. Coach noted that
 * burying it in the footer was suppressing readership, so it now sits between
 * Method and About as a low-emphasis text link (kept off the CTA cluster so
 * the bar doesn't get noisy).
 *
 * Phase 36 \u2014 "Pricing" restored to primary nav. The standalone /pricing page
 * is now the canonical destination for "what does it cost?" — surfacing free
 * tiers, self-serve products, 1:1 coaching, and team/school workshops all on
 * one page (transparent across every tier).
 *
 * Demoted from primary nav (moved to footer):
 *   Try AI Free \u2014 reachable via /app/login from footer; not a top-of-funnel ask.
 *
 * Desktop breakpoint moved from md (768px) \u2192 lg (1024px) because the audience
 * link inventory is denser; cramming 6 text links + 3 CTAs at 768px reads as
 * noise. Tablets get the hamburger \u2014 cleaner.
 */

const navLinks = [
  { label: 'Athletes',     href: '/athletes' },
  { label: 'Parents',      href: '/parents' },
  { label: 'Coaches',      href: '/coaches' },
  { label: 'The Apex Six', href: '/method' },
  { label: 'Pricing',      href: '/pricing' },
  { label: 'Journal',      href: '/blog' },
];
// Secondary links collapsed behind a "More" affordance to keep cold-visitor
// nav under 5 items. Rendered as plain links inside the mobile menu and as a
// "More ▾" dropdown on desktop.
const moreLinks = [
  { label: 'Schools', href: '/schools' },
  { label: 'About',   href: '/about' },
];

export const Navbar = ({ calendlyLink, logoUrl, topAccessory = null }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const isAuthed = !authLoading && !!user;
  const firstName = (user?.name || '').trim().split(' ')[0];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const trackNavClick = (label, location) => {
    try {
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'nav_cta_click', { label, location });
      }
    } catch {
      /* analytics is best-effort */
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-[0_1px_3px_rgba(0,0,0,0.06)] border-b border-border/30'
          : 'bg-transparent'
      }`}
      data-testid="navbar"
    >
      {/* Optional top accessory (e.g. campaign banner). Renders ABOVE the nav
          row inside the same fixed container so the banner + navbar stack as
          one cohesive top strip and never overlap. */}
      {topAccessory}
      <nav className="w-full px-5 sm:px-8 md:px-10 lg:px-12 xl:px-16 flex items-center justify-between h-[60px] sm:h-[64px]" aria-label="Main navigation">
        <a href="/" className="flex items-center gap-2.5 shrink-0" data-testid="nav-logo">
          <img
            src={logoUrl}
            alt="Apex Athletic Mind Consulting"
            className="h-9 w-9 object-contain rounded-md"
          />
          <div className="hidden sm:block leading-tight">
            <span className={`font-heading font-bold text-[15px] tracking-tight block transition-colors duration-300 ${scrolled ? 'text-foreground' : 'text-white'}`}>
              Apex Athletic Mind
            </span>
            <span className={`font-mono-accent text-[8px] uppercase tracking-[0.18em] transition-colors duration-300 ${scrolled ? 'text-muted-foreground' : 'text-white/40'}`}>
              Consulting
            </span>
          </div>
        </a>

        <div className="hidden lg:flex items-center gap-5 xl:gap-7">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className={`relative text-[13px] font-medium pb-1 transition-colors duration-200 after:pointer-events-none after:absolute after:left-0 after:right-0 after:bottom-0 after:h-[2px] after:origin-left after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-[220ms] after:ease-out ${
                scrolled
                  ? 'text-muted-foreground hover:text-foreground after:bg-[#004C54]'
                  : 'text-white/70 hover:text-white after:bg-[#4FBCC7]'
              }`}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => trackNavClick(link.label, 'desktop_nav')}
            >
              {link.label}
            </a>
          ))}
          {/* "More ▾" — Schools + About collapsed behind a hover/focus dropdown
              so the primary nav stays under 5 items for cold visitors. */}
          <div className="relative group">
            <button
              type="button"
              className={`relative text-[13px] font-medium pb-1 inline-flex items-center gap-0.5 transition-colors duration-200 ${
                scrolled ? 'text-muted-foreground hover:text-foreground' : 'text-white/70 hover:text-white'
              }`}
              data-testid="nav-more-trigger"
              aria-haspopup="true"
            >
              More <span aria-hidden="true" className="text-[9px] mt-[1px]">▾</span>
            </button>
            <div
              className="invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:visible group-focus-within:opacity-100 group-focus-within:translate-y-0 transition-all duration-150 absolute right-0 top-full pt-2 z-50"
              role="menu"
            >
              <div className="min-w-[140px] bg-white text-foreground rounded-lg shadow-xl border border-border/40 py-1.5">
                {moreLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="block px-4 py-1.5 text-[13px] font-medium hover:bg-secondary/60"
                    data-testid={`nav-more-${link.label.toLowerCase()}`}
                    onClick={() => trackNavClick(link.label, 'desktop_nav_more')}
                    role="menuitem"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="hidden lg:flex items-center gap-3 shrink-0">
          {/* Phase 30 — "Get in the Zone" music trigger. Click opens a
              centered playlists modal (no auto-open). Sits at the start of
              the CTA cluster so it's visible but doesn't crowd the booking
              button. 10px-equivalent breathing room handled by the gap-3
              parent. */}
          {/* Music trigger — member feature. Hidden for cold visitors so it
              doesn't compete with the conversion CTAs above the fold. */}
          {isAuthed && <MusicNavTrigger scrolled={scrolled} />}
          {/* Returning-client login affordance.
              Logged OUT → compact "Sign In" pill (Phase 35 restore — visible
                           on every page so returning members never hunt).
              Logged IN  → friendly first-name pill linking back into the app. */}
          {!isAuthed && (
            <a
              href="/app/login"
              data-testid="nav-sign-in"
              onClick={() => trackNavClick('Sign In', 'desktop_nav')}
              className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[12px] font-bold uppercase tracking-[0.14em] transition-all ${
                scrolled
                  ? 'text-[#004C54] hover:bg-[#004C54]/10 ring-1 ring-[#004C54]/25 hover:ring-[#004C54]/45'
                  : 'text-white/85 hover:text-white hover:bg-white/10 ring-1 ring-white/25 hover:ring-white/45'
              }`}
            >
              <LogIn className="h-3.5 w-3.5" />
              <span>Sign In</span>
            </a>
          )}
          {isAuthed && (
            <a
              href="/app/dashboard"
              data-testid="nav-open-app"
              onClick={() => trackNavClick('Open App', 'desktop_nav')}
              className={`inline-flex items-center gap-1.5 h-9 pl-2 pr-3 rounded-full text-[12px] font-bold uppercase tracking-[0.14em] transition-all ${
                scrolled
                  ? 'bg-[#004C54]/8 text-[#004C54] hover:bg-[#004C54]/14'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${
                scrolled ? 'bg-[#004C54] text-white' : 'bg-white/25 text-white'
              }`}>
                {(firstName || 'U').charAt(0).toUpperCase()}
              </span>
              <span>{firstName ? `Hi, ${firstName}` : 'Open App'}</span>
            </a>
          )}

          <a
            href="/quiz"
            className={`text-[12px] font-bold uppercase tracking-[0.12em] px-2.5 py-1 rounded-md transition-colors ${
              scrolled
                ? 'bg-[#004C54]/10 text-[#004C54] hover:bg-[#004C54]/15'
                : 'bg-white/15 text-white hover:bg-white/25'
            }`}
            data-testid="nav-link-quiz"
            onClick={() => trackNavClick('Quiz', 'desktop_nav')}
          >
            Mindset Quiz
          </a>
          {/* The Locker Room is a member-only destination — hidden for cold
              visitors so it doesn't read like a podcast/community to first-
              time guests. Returning members still see it. */}
          {isAuthed && (
            <a
              href="/locker-room"
              data-testid="nav-locker-room-pill"
              onClick={() => trackNavClick('Locker Room', 'desktop_nav')}
              className={`group inline-flex items-center gap-1.5 h-9 pl-2.5 pr-3.5 rounded-full text-[12px] font-bold uppercase tracking-[0.14em] transition-all duration-200 active:scale-[0.97] ${
                scrolled
                  ? 'bg-[#0B0C0D] text-white hover:bg-[#1a1d1e] shadow-sm ring-1 ring-[#4FBCC7]/30'
                  : 'bg-white/12 backdrop-blur-sm text-white hover:bg-white/20 ring-1 ring-[#4FBCC7]/40'
              }`}
            >
              <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${
                scrolled ? 'bg-[#004C54]' : 'bg-[#4FBCC7]/30'
              }`}>
                <Music className="h-2.5 w-2.5 text-white" strokeWidth={2.5} />
              </span>
              <span>The Locker Room</span>
            </a>
          )}
          <Button
            asChild
            className="bg-[#004C54] hover:bg-[#003a40] text-white font-semibold h-9 px-4 text-[13px] rounded-md active:scale-[0.98] shadow-sm"
          >
            <a
              href={calendlyLink}
              data-testid="nav-cta-button"
              onClick={() => trackNavClick('Book Discovery Call', 'desktop_nav')}
            >
              Book a Call
            </a>
          </Button>
        </div>

        <button
          className={`lg:hidden p-2 -mr-2 rounded-lg transition-colors ${scrolled ? 'hover:bg-secondary/60 text-foreground' : 'hover:bg-white/10 text-white'}`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle navigation menu"
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-b border-border/40 shadow-lg" data-testid="mobile-menu">
          <div className="max-w-[1120px] mx-auto px-5 py-3 flex flex-col gap-0.5">
            {/* Returning-client login affordance.
                Always near the very top of the mobile menu so it's never missed. */}
            {isAuthed ? (
              <a
                href="/app/dashboard"
                data-testid="mobile-nav-open-app"
                onClick={() => { setMobileOpen(false); trackNavClick('Open App', 'mobile_nav'); }}
                className="flex items-center justify-between gap-3 px-4 h-11 mb-1 rounded-xl bg-[#004C54]/10 text-[#004C54] font-bold text-[13px] uppercase tracking-[0.12em] active:scale-[0.98]"
              >
                <span className="inline-flex items-center gap-2.5">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#004C54] text-white text-[10px] font-bold">
                    {(firstName || 'U').charAt(0).toUpperCase()}
                  </span>
                  {firstName ? `Hi, ${firstName} — Open App` : 'Open App'}
                </span>
                <UserIcon className="h-4 w-4" strokeWidth={2.25} />
              </a>
            ) : (
              <a
                href="/app/login"
                data-testid="mobile-nav-sign-in"
                onClick={() => { setMobileOpen(false); trackNavClick('Sign In', 'mobile_nav'); }}
                className="flex items-center justify-between gap-3 px-4 h-11 mb-1 rounded-xl border-2 border-[#004C54]/25 text-[#004C54] font-bold text-[13px] uppercase tracking-[0.12em] active:scale-[0.98] hover:bg-[#004C54]/5"
              >
                <span className="inline-flex items-center gap-2">
                  <LogIn className="h-4 w-4" strokeWidth={2.25} />
                  Sign In — Returning Clients
                </span>
                <span className="font-mono-accent text-[9px] tracking-[0.18em] text-[#004C54]/55 normal-case">Apex AI Coach</span>
              </a>
            )}

            {/* Locker Room pill — top of mobile menu for prominence */}
            <a
              href="/locker-room"
              data-testid="mobile-nav-locker-room-pill"
              onClick={() => { setMobileOpen(false); trackNavClick('Locker Room', 'mobile_nav'); }}
              className="inline-flex items-center justify-between gap-3 px-4 h-12 mb-2 rounded-xl bg-[#0B0C0D] text-white font-bold text-[13.5px] uppercase tracking-[0.14em] shadow-md ring-1 ring-[#4FBCC7]/40 active:scale-[0.98]"
            >
              <span className="inline-flex items-center gap-2.5">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#004C54]">
                  <Music className="h-3 w-3 text-white" strokeWidth={2.5} />
                </span>
                The Locker Room
              </span>
              <span className="font-mono-accent text-[9px] tracking-[0.18em] text-[#4FBCC7] normal-case">Members</span>
            </a>
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium py-2.5 px-3 rounded-md hover:bg-secondary/60 transition-colors text-foreground"
                onClick={() => { setMobileOpen(false); trackNavClick(link.label, 'mobile_nav'); }}
                data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
            <a
              href="/quiz"
              className="text-sm font-bold uppercase tracking-[0.12em] py-2.5 px-3 mt-1 rounded-md bg-[#004C54]/10 text-[#004C54] hover:bg-[#004C54]/15 transition-colors"
              onClick={() => { setMobileOpen(false); trackNavClick('Quiz', 'mobile_nav'); }}
              data-testid="mobile-nav-link-quiz"
            >
              Mindset Quiz
            </a>
            {/* Phase 30 — "Get in the Zone" lives in the mobile menu so non-
                desktop visitors can still reach the Apex playlists. Closes
                the menu on click; the modal handles its own dismissal. */}
            <div className="mt-1" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-music-wrap">
              <MusicNavTrigger
                scrolled
                className="w-full justify-start h-11 px-3 text-[13px] text-foreground hover:bg-secondary/60"
              />
            </div>
            <Button
              asChild
              variant="outline"
              className="mt-2 w-full h-11 border-2 border-[#004C54] text-[#004C54] hover:bg-[#004C54]/10 font-semibold"
            >
              <a
                href="/app/login"
                onClick={() => { setMobileOpen(false); trackNavClick('Try AI Free', 'mobile_nav'); }}
                data-testid="mobile-nav-try-ai"
              >
                Try the AI Coach Free
              </a>
            </Button>
            <Button
              asChild
              className="bg-[#004C54] hover:bg-[#003a40] text-white font-semibold mt-2 w-full h-11"
            >
              <a
                href={calendlyLink}
                onClick={() => { setMobileOpen(false); trackNavClick('Book Discovery Call', 'mobile_nav'); }}
                data-testid="mobile-nav-cta-button"
              >
                Book a Discovery Call
              </a>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
